/**
 * Wanted by the police: from one star to five, what raises it, how it is
 * shaken off, and how it ends in an arrest. Pure; `main.ts` says what the
 * player did and whether the police can see them, and `three/traffic.ts`
 * sends the police after them.
 *
 * - **Offences.** Speeding (more than `SPEEDING` over the road's speed) only
 *   counts if a police car sees it: one star. Crashing into a car does
 *   too: a star more. Knocking someone down, or crashing into a police car,
 *   counts whether seen or not (there are witnesses, or the police are
 *   there): at least two stars, and two more for a police car.
 * - **The chase.** Every `ESCALATE` seconds the police keep the player in
 *   sight, it goes up a star, to four; only more offences make it five.
 * - **Being seen.** The police see the player within their sight, in a
 *   clear line (`main.ts` asks the world what stands between). In another
 *   vehicle than they were last seen in, or on foot having been in one, they
 *   know them only from close by (`RECOGNISE`); lying low at a garage door,
 *   only from right beside it (`LYING_LOW_SEEN`). See `seesWanted`.
 * - **Getting away.** Out of every police car's sight (and the
 *   helicopter's), and well away from where they last saw the player
 *   (`known` says whether they still know roughly where), for
 *   `escapeTime(stars)` seconds, longer the more stars, the police give up.
 *   Standing still out of sight does not shake them off: they come. Having
 *   changed vehicle out of their sight, or lying low, they no longer know
 *   where; lying low, they give up in half the time.
 * - **Arrest.** Standing still with a pursuing police car beside them for
 *   `ARREST_TIME` seconds, the player is arrested: fined `fine(stars)`
 *   kronor, and no longer wanted.
 */

export type Offence = 'speeding' | 'crash' | 'policeCrash' | 'person' | 'carjack';

export interface Wanted {
  stars: number;
  /** Seconds out of sight of the police. */
  unseen: number;
  /** Seconds of being chased in sight since the last star the chase gave. */
  chased: number;
  /** Seconds standing caught. */
  caught: number;
}

/** More than this over the road's speed, m/s (30 km/h), is speeding the police stop you for. */
export const SPEEDING = 30 / 3.6;
/** Seconds of a chase in sight before it goes up a star, and the most stars a chase alone gives. */
export const ESCALATE = 45;
const CHASE_MOST = 4;
/** Seconds standing caught before the arrest. */
export const ARREST_TIME = 3;
export const MOST_STARS = 5;

export function notWanted(): Wanted {
  return { stars: 0, unseen: 0, chased: 0, caught: 0 };
}

/** How long out of sight shakes the police off, seconds: 18 s at one star, 50 s at five; half that lying low. */
export function escapeTime(stars: number, lyingLow = false): number {
  return (10 + 8 * stars) * (lyingLow ? 0.5 : 1);
}

/** How near the police must be to know the player in another vehicle than they last saw them in, metres. */
export const RECOGNISE = 25;
/** How near they must be to see the player lying low at a garage door, metres. */
export const LYING_LOW_SEEN = 8;

/**
 * Whether the police, `distance` metres off with a clear line to the
 * player and seeing as far as `sight`, see them as the one they want:
 * `changed` vehicle since they were last seen, or `lyingLow`, they must be
 * near.
 */
export function seesWanted(distance: number, sight: number, changed: boolean, lyingLow: boolean): boolean {
  const reach = lyingLow ? LYING_LOW_SEEN : changed ? Math.min(sight, RECOGNISE) : sight;
  return distance < reach;
}

/** The fine for being caught at `stars`, kronor. */
export function fine(stars: number): number {
  return 500 * stars;
}

/** Whether going `speed` on a road whose speed is `limit` (both m/s) is speeding the police stop you for. */
export function speeding(speed: number, limit: number | undefined): boolean {
  return limit !== undefined && speed > limit + SPEEDING;
}

/** An offence, `seen` by the police or not: how many stars it raises the player to. Returns whether it rose. */
export function offend(w: Wanted, offence: Offence, seen: boolean): boolean {
  const before = w.stars;
  switch (offence) {
    case 'speeding':
      if (seen) w.stars = Math.max(w.stars, 1);
      break;
    case 'crash':
      if (seen) w.stars = Math.max(1, w.stars + 1);
      break;
    case 'person':
      w.stars = Math.max(2, w.stars + 1);
      break;
    case 'carjack':
      if (seen) w.stars = Math.max(2, w.stars + 1);
      break;
    case 'policeCrash':
      w.stars = Math.max(2, w.stars + 2);
      break;
  }
  w.stars = Math.min(MOST_STARS, w.stars);
  if (w.stars > before) w.unseen = 0;
  return w.stars > before;
}

/**
 * On by `dt` seconds, `seen` by the police or not, `caught` (standing still
 * with a pursuing police car beside them) or not, `lyingLow` or not. Says
 * when the chase ends, one way or the other.
 */
export function stepWanted(w: Wanted, dt: number, seen: boolean, caught: boolean, known = false, lyingLow = false): 'arrested' | 'escaped' | undefined {
  if (w.stars === 0) return undefined;
  w.caught = caught ? w.caught + dt : 0;
  if (w.caught >= ARREST_TIME) {
    Object.assign(w, notWanted());
    return 'arrested';
  }
  if (seen) {
    w.unseen = 0;
    w.chased += dt;
    if (w.chased >= ESCALATE) {
      w.chased = 0;
      if (w.stars < CHASE_MOST) w.stars++;
    }
    return undefined;
  }
  if (known) {
    w.unseen = 0;
    return undefined;
  }
  w.unseen += dt;
  if (w.unseen >= escapeTime(w.stars, lyingLow)) {
    Object.assign(w, notWanted());
    return 'escaped';
  }
  return undefined;
}

/** How many police chase the player at `stars`: two at one star, eight at five. */
export function pursuers(stars: number): number {
  return stars <= 0 ? 0 : Math.min(8, Math.round(0.5 + stars * 1.5));
}
